import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWorkingCapitalFiltersContext } from './WorkingCapitalFiltersProvider';
import { getSavingsCalculationData } from 'utils/apiRequests';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemValue,
} from 'contexts/CategoryDashboardCharts/DashboardChart.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import {
	editThresholdSavingsCalculation,
	getThresholdSavingsCalculation,
} from 'utils/apiRequests';
import { SettingsContext } from 'contexts/SettingsContext';

const pageSize = 10;

const SavingCalculation = createContext();

export const SavingCalculationProvider = ({ children }) => {
	const {
		currentBreakDownFilter,
		currentFilterValuesSelected,
		dateRangeData,
	} = useWorkingCapitalFiltersContext();
	const { hideDecimals } = useContext(SettingsContext);
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [tableData, setTableData] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [isWaccEnabled, setIsWaccEnalbled] = useState(false);
	const [savingsCalculationSettings, setSavingsCalculationSettings] = useState({
		DelayLow: null,
		DelayHigh: null,
		EarlyLow: null,
		EarlyHigh: null,
		WCLow: null,
		WCHigh: null,
		PCardLow: null,
		PCardHigh: null,
		OpportuneLow: null,
		OpportuneHigh: null,
		WACC: null,
	});
	// const dateRange = {
	// 	srcRange: { from: { year: 2021, month: 1 }, to: { year: 2024, month: 12 } },
	// 	stateRange: {
	// 		from: { month: 'jan', monthNumber: 1, year: 2023 },
	// 		to: { month: 'dec', monthNumber: 12, year: 2023 },
	// 	},
	// 	dateFrom: '2023-01-01',
	// 	dateTo: '2023-12-31',
	// };

	const [options, setOptions] = useState({
		chart: {
			type: 'waterfall',
			width: null,
		},

		title: {
			text: null,
		},
		exporting: {
			enabled: false,
		},
		xAxis: {
			type: 'category',
			reversed: true,
		},

		yAxis: {
			title: {
				text: '',
			},
			gridLineWidth: 0,
			// min: 0,
			labels: {
				enabled: false,
			},
		},

		legend: {
			enabled: false,
		},
		credits: {
			enabled: false, // Disable the Highcharts.com watermark/footer
		},

		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						{/* <TooltipItemTitle>{this.x}</TooltipItemTitle> */}
						{this.points.map(item => (
							<TooltipItem key={`${item.key}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>{item.key}</TooltipItemName>
								</span>
								<TooltipItemValue>
									{formatSpendWithDecimalAndSymbol(
										item.point.y,
										'$',
										hideDecimals,
										2,
										true
									).toLocaleUpperCase()}
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
			borderRadius: 2,
		},
		series: [
			{
				borderWidth: 0,
				data: [],
				dataLabels: {
					enabled: true,
					verticalAlign: 'top',
					y: -23,
					formatter: function() {
						return renderToString(
							formatSpendWithDecimalAndSymbol(
								this.y,
								'$',
								hideDecimals,
								2,
								true
							).toLocaleUpperCase()
						);
					},
					style: {
						color: 'black', // Change this to the desired color
					},
				},
				pointPadding: 0,
			},
		],
	});

	// Function to calculate the difference in months between two dates in YYYY-MM-DD format
	function monthDifference(fromDateStr, toDateStr) {
		// Convert date strings into Date objects
		let fromDate = new Date(fromDateStr);
		let toDate = new Date(toDateStr);

		// Extract the year and month from both dates
		let fromYear = fromDate.getFullYear();
		let fromMonth = fromDate.getMonth(); // 0-indexed

		let toYear = toDate.getFullYear();
		let toMonth = toDate.getMonth(); // 0-indexed

		// Calculate the difference in years and months
		let yearDiff = toYear - fromYear;
		let monthDiff = toMonth - fromMonth;

		// If monthDiff is negative, adjust by subtracting 1 year and adding 12 months
		if (monthDiff < 0) {
			yearDiff--;
			monthDiff += 12;
		}

		// Return the total difference in months
		return yearDiff * 12 + monthDiff;
	}

	useEffect(
		() => {
			if (data) {
				let tempTableData = [];
				for (let item in data) {
					let tempObj = {
						filterBy: item,
						rowSpan: 6,
						collapse: false,
						savingsLever: [
							'Change payment run frequency:',
							'Minimize early payments to suppliers:',
							'Extend Supplier payment terms:',
							'Harmonization opportunity',
							'Expand use oF P-card/Virtual card program',
						],
						description: [
							'Delay payment run frequency by 1 week',
							'In 2023, WB made £3M of early payment to suppliers, Reducing that by 75-100%##Improve overall Procure to Pay process',
							'Negotiate payment terms with larger suppliers##Restart clock for discrepant invoices',
							'opportunity',
							'Move smaller suppliers to P-Card',
						],
						savingsLow: [],
						savingsHigh: [],
						savingsAvg: [],
					};
					const waccSavingsAvg = savingsAvg => {
						let diff =
							monthDifference(dateRangeData.dateFrom, dateRangeData.dateTo) + 1;
						let finalSum =
							(((savingsCalculationSettings.WACC / 100) * diff) / 12) *
							savingsAvg;
						return finalSum;
					};
					for (let item1 in data[item]) {
						if (item1.includes('earlyPayment')) {
							if (item1.includes('earlyPayment75')) {
								tempObj.savingsLow[1] = data[item][item1];
							} else if (item1.includes('earlyPayment100')) {
								tempObj.savingsHigh[1] = data[item][item1];
							} else {
								tempObj.savingsAvg[1] = isWaccEnabled
									? waccSavingsAvg(data[item][item1])
									: data[item][item1];
							}
						} else if (item1.includes('delayPayment')) {
							if (item1.includes('delayPaymentBy3')) {
								tempObj.savingsLow[0] = data[item][item1];
							} else if (item1.includes('delayPaymentBy5')) {
								tempObj.savingsHigh[0] = data[item][item1];
							} else {
								tempObj.savingsAvg[0] = isWaccEnabled
									? waccSavingsAvg(data[item][item1])
									: data[item][item1];
							}
						} else if (
							item1.includes('pCardReduction') &&
							!item1.includes('pCardReductionTotal')
						) {
							if (item1.includes('pCardReduction50')) {
								tempObj.savingsLow[4] = data[item][item1];
							} else if (item1.includes('pCardReduction75')) {
								tempObj.savingsHigh[4] = data[item][item1];
							} else {
								tempObj.savingsAvg[4] = isWaccEnabled
									? waccSavingsAvg(data[item][item1])
									: data[item][item1];
							}
						} else if (
							item1.includes('wcReduction') &&
							!item1.includes('wcReductionTotal')
						) {
							if (item1.includes('wcReduction25')) {
								tempObj.savingsLow[2] = data[item][item1];
							} else if (item1.includes('wcReduction50')) {
								tempObj.savingsHigh[2] = data[item][item1];
							} else {
								tempObj.savingsAvg[2] = isWaccEnabled
									? waccSavingsAvg(data[item][item1])
									: data[item][item1];
							}
						} else if (
							item1.includes('opportunityReduction') &&
							!item1.includes('opportunityReductionTotal')
						) {
							if (item1.includes('opportunityReductionLow')) {
								tempObj.savingsLow[3] = data[item][item1];
							} else if (item1.includes('opportunityReductionHigh')) {
								tempObj.savingsHigh[3] = data[item][item1];
							} else {
								tempObj.savingsAvg[3] = isWaccEnabled
									? waccSavingsAvg(data[item][item1])
									: data[item][item1];
							}
						}
					}
					tempTableData.push(tempObj);
				}
				const returnSum = index =>
					tempTableData.reduce((sum, item) => sum + item.savingsAvg[index], 0);

				let seriesData = [...options.series];
				seriesData[0].data = [
					{
						name: 'Expand use of P-Card/virtual card program',
						y: returnSum(4),
						color: 'rgb(218, 241, 208)',
					},
					{
						name: 'Harmonization opportunity',
						y: returnSum(3),
						color: 'rgb(107, 138, 188)',
					},
					{
						name: 'Extend supplier payment terms',
						y: returnSum(2),
						color: 'rgb(46, 224, 202)',
					},

					{
						name: 'Minimize early payment to suppliers',
						y: returnSum(1),
						color: 'rgb(29, 78, 216)',
					},
					{
						name: 'Change payment run frequency',
						y: returnSum(0),
						color: 'rgb(96, 165, 250)',
					},

					{
						name: 'Total',
						isSum: true,
						color: 'rgb(173, 216, 230)',
					},
				];
				setOptions(prevState => ({
					...prevState,
					series: seriesData,
				}));
				setTableData(tempTableData);
				if (isWaccEnabled) {
					setTimeout(() => {
						setIsLoading(false);
					}, 500);
				} else {
					setIsLoading(false);
				}
			}
		},
		[data, isWaccEnabled]
	);

	function getSavingCalculationData() {
		setIsLoading(true);
		let payload = {
			Dimension: currentBreakDownFilter.value,
			Values: currentFilterValuesSelected,
			dateFrom: dateRangeData.dateFrom,
			dateTo: dateRangeData.dateTo,
			DelayLow: savingsCalculationSettings.DelayLow,
			DelayHigh: savingsCalculationSettings.DelayHigh,
			EarlyLow: savingsCalculationSettings.EarlyLow / 100,
			EarlyHigh: savingsCalculationSettings.EarlyHigh / 100,
			WCLow: savingsCalculationSettings.WCLow / 100,
			WCHigh: savingsCalculationSettings.WCHigh / 100,
			PCardLow: savingsCalculationSettings.PCardLow / 100,
			PCardHigh: savingsCalculationSettings.PCardHigh / 100,
			OpportuneLow: savingsCalculationSettings.OpportuneLow / 100,
			OpportuneHigh: savingsCalculationSettings.OpportuneHigh / 100,
			WCC: savingsCalculationSettings.WACC / 100,
		};
		getSavingsCalculationData(payload)
			.then(response => {
				setData(response);
			})
			.catch(error => {
				console.log(error, 'Error Message');
				setIsLoading(false);
			});
	}

	useEffect(
		() => {
			if (
				currentFilterValuesSelected.length > 0 &&
				currentBreakDownFilter.value &&
				dateRangeData.dateFrom &&
				dateRangeData.dateTo
			) {
				getSavingCalculationData();
			}
		},
		[currentFilterValuesSelected, currentBreakDownFilter.value, dateRangeData]
	);

	useEffect(() => {
		getThresholdSavingsCalculation()
			.then(response => {
				setSavingsCalculationSettings({
					DelayLow: response.DelayLow,
					DelayHigh: response.DelayHigh,
					EarlyLow: response.EarlyLow * 100,
					EarlyHigh: response.EarlyHigh * 100,
					WCLow: response.WCLow * 100,
					WCHigh: response.WCHigh * 100,
					PCardLow: response.PCardLow * 100,
					PCardHigh: response.PCardHigh * 100,
					OpportuneLow: response.OpportuneLow * 100,
					OpportuneHigh: response.OpportuneHigh * 100,
					WACC: response.WCC * 100,
				});
			})
			.catch(error => {
				console.log(error);
			});
	}, []);

	const saveSavingCalculationFilters = async setCloseSettings => {
		setIsSaving(true);
		let payload = {
			DelayLow: savingsCalculationSettings.DelayLow,
			DelayHigh: savingsCalculationSettings.DelayHigh,
			EarlyLow: savingsCalculationSettings.EarlyLow / 100,
			EarlyHigh: savingsCalculationSettings.EarlyHigh / 100,
			WCLow: savingsCalculationSettings.WCLow / 100,
			WCHigh: savingsCalculationSettings.WCHigh / 100,
			PCardLow: savingsCalculationSettings.PCardLow / 100,
			PCardHigh: savingsCalculationSettings.PCardHigh / 100,
			OpportuneLow: savingsCalculationSettings.OpportuneLow / 100,
			OpportuneHigh: savingsCalculationSettings.OpportuneHigh / 100,
			WCC: savingsCalculationSettings.WACC / 100,
		};
		await editThresholdSavingsCalculation(payload);
		setIsSaving(false);
		setCloseSettings(false);
		getSavingCalculationData();
		// handleRefreshGrid('Refresssed');
	};

	return (
		<SavingCalculation.Provider
			value={{
				data,
				isLoading,
				setIsLoading,
				options,
				currentPage,
				setCurrentPage,
				pageSize,
				tableData,
				setTableData,
				isSaving,
				setIsSaving,
				savingsCalculationSettings,
				setSavingsCalculationSettings,
				saveSavingCalculationFilters,
				getSavingCalculationData,
				isWaccEnabled,
				setIsWaccEnalbled,
			}}
		>
			{children}
		</SavingCalculation.Provider>
	);
};

export const useSavingCalculationContext = () => {
	return useContext(SavingCalculation);
};
